import type { ReasonCode } from '@/api/types';
import { REASON_CODES_ORDERED, REASON_CODE_META } from '@/lib/reason-codes';
import { cn } from '@/lib/utils';

/** Multi-select checkbox grid for the reject-flow reason codes.
 *
 *  Controlled component — caller owns the `value` array and reacts to
 *  toggle via `onChange`. Used by the single-finding reject card on
 *  the run page, and by the bulk-reject dialog.
 *
 *  Layout: two-column grid on >=sm so all 12 codes fit above the fold,
 *  single column below. Each row is a label + native checkbox with the
 *  full tooltip text on hover (HTML `title` attribute — Tailwind-free,
 *  no positioning maths). */
export function ReasonCodesPicker({
  value,
  onChange,
  disabled = false,
  className,
}: {
  value: ReasonCode[];
  onChange: (next: ReasonCode[]) => void;
  disabled?: boolean;
  className?: string;
}) {
  const selected = new Set(value);

  const toggle = (code: ReasonCode) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    // Preserve the canonical display order in the emitted array so the
    // backend de-dupe is irrelevant + the rendered pills are stable
    // regardless of the order the practitioner clicked.
    onChange(REASON_CODES_ORDERED.filter((c) => next.has(c)));
  };

  return (
    <fieldset
      className={cn('grid gap-1.5 rounded-md border border-border bg-secondary/20 p-3', className)}
      disabled={disabled}
    >
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Reason codes (optional, multi-select)
      </legend>
      <div className="grid gap-x-3 gap-y-1.5 sm:grid-cols-2">
        {REASON_CODES_ORDERED.map((code) => {
          const meta = REASON_CODE_META[code];
          const checked = selected.has(code);
          return (
            <label
              key={code}
              title={meta.tooltip}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-sm transition-colors hover:bg-secondary/60',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(code)}
                disabled={disabled}
                className="mt-0.5 size-4 shrink-0 accent-brand-royal"
              />
              <span className="grid min-w-0">
                <span className="flex items-baseline gap-1.5">
                  <span
                    className={cn(
                      'font-medium',
                      checked ? 'text-brand-navy' : 'text-brand-slate',
                    )}
                  >
                    {meta.label}
                  </span>
                  {meta.dispositionHint && (
                    <span
                      className={cn(
                        'rounded-full px-1.5 text-[10px] font-semibold uppercase tracking-wide',
                        meta.dispositionHint === 'reject'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-amber-500/15 text-amber-700',
                      )}
                    >
                      {meta.dispositionHint === 'reject' ? 'dispositive' : 'verify first'}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-brand-slate/80 line-clamp-1">
                  {meta.tooltip}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Render a list of selected reason codes as small badge pills. Used
 *  on the finding detail panel next to the rejection note. */
export function ReasonCodePills({
  codes,
  className,
}: {
  codes: readonly ReasonCode[];
  className?: string;
}) {
  if (codes.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {codes.map((code) => {
        const meta = REASON_CODE_META[code];
        return (
          <span
            key={code}
            title={meta.tooltip}
            className="inline-flex items-center rounded-full bg-brand-royal/10 px-2 py-0.5 text-[11px] font-medium text-brand-royal"
          >
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}
